// Billing pricing — the pure half of the Billing tab's transaction pipeline.
//
// billing.js used to derive all of this inside the component: the stored
// billing rows parsed into billed/meta/settings maps, the job's JDE labor
// classes and equipment tiers, the rate sheet lookups those bill at, and the
// big per-line pricing pass that turns a cost history row into a billing
// transaction (`txns`). The Overview VoltaGrid tab now needs the same
// numbers — its revenue is the job's billable amount — so the derivations
// live here, with no React and no network, and the tab calls them from its
// memos. One pricing pass, two readers: the Billing grid and the Overview
// can't disagree about what a line bills for.
//
// Nothing here formats for the screen; the component keeps its own money()
// and badges.

import moment from 'moment';
import { pNum } from '../db/company';
import { mergeRateSchedule, stRate, otRate, round2, DEFAULT_SETTINGS } from './billingRates';
import { DEFAULT_RATE_CONFIG } from './billingRateSchedule';
import { parseLaborMap, suggestVoltaClass, normClass } from './billingLaborMap';
import { parseEquipMap, suggestEquipRow, tierDescr } from './billingEquipMap';
import { normPt, ptRuleMatch, isPassThru } from './billingPassThru';
import { contractRule, contractRuleById, contractOn, contractFor, isFreightDs } from './billingContract';
import { fuelLineAmt } from './billingFuel';
import { rentUsageFrom, rentUsageCostRows, rentUsageTiers, rentUnitCode, RENTAL_CT } from './billingRentalUsage';

// Cost classes match the sideMenu JTD buckets. Revenue rows (40000s) and the
// 60900 overhead allocation are not billable transactions and are excluded.
export const bucketOf = (code) => {
    const c = String(code || '');
    if (c >= '51000' && c <= '51999') return 'Labor';
    if (c >= '52000' && c <= '52999') return 'Equipment';
    if (c >= '53000' && c <= '53999') return 'Materials';
    if (c >= '54000' && c <= '54999') return 'Subcontractors';
    return 'Other';
};

// Cost codes that never belong on an invoice. Their rows stay visible so the
// cost picture is complete, but they carry a NOT BILLABLE tag on the cost code
// and bill at $0 — they can't be put on an invoice or marked billable.
export const NON_BILLABLE_CC = ['100105'];

// Labor cost types that actually get invoiced.
// The rest of the 51xxx range (burden, taxes, per diem …) is labor cost but
// not labor billing, so those rows never show on this tab.
export const LABOR_BILLABLE_CT = ['51100', '51200', '51500'];

// Cost types that will never be billed (Seth, 9/1) — excluded from the tab
// at the source, so they don't touch the headline stats or exports either.
export const NEVER_BILLED_CT_PREFIX = ['513', '514', '516', '517'];

// Events that set the line's review status — the newest one is the stamp
// shown in the REVIEW column.
export const REVIEW_EVENTS = ['rev', 'flag', 'clr'];

// Rows saved before the workflow stages used 'Sent'; the client hand-off was
// 'Sent to Client' before it moved onto the FTMS button; before the Invoiced
// rename, 'Paid'. The AR stage is retired, so a summary parked in it (or in
// its older 'With Chandra' spelling) comes back as a Draft to upload.
export const normInvStatus = (s) => (
    s === 'Sent' || s === 'Sent to Client' ? 'Uploaded to FTMS'
    : s === 'With Chandra' || s === 'With AR' ? 'Draft'
    : s === 'Paid' ? 'Invoiced'
    : s || 'Draft');

// A summary header is the 'invoice' row whose id is inv_<no>. The field is
// shared with the Invoice Workbook's terms and exception tables
// (invoice_terms, invoice_cls_*, invoice_person_*, invoice_unit_*) and its
// drafts (invoice_draft_*), none of which is a summary: read as one they
// showed as $0.00 Not Sent rows with no number, deleting as "undefined".
export const isSummaryRow = (x) => String(x?.Forecast_Row_ID || '').startsWith('inv_');

// Legacy row identity: a short hash of the row's own fields. Identical rows
// are interchangeable, so an occurrence counter on the composite is enough
// to keep keys unique.
export const hash = (s) => {
    let h = 5381;
    for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    return h.toString(36);
};

// SQL dates are naive server-local datetimes — format in UTC so they don't
// shift a day in other timezones.
export const fDate = (d) => (d ? moment.utc(d).format('M/D/YY') : '');

// "Last, First" → "First Last". Only a string that is exactly one
// comma-separated pair of name-ish tokens counts — payroll descriptions
// that aren't a person ("Regular Pay", "Per DiemNT") fall through.
export const nameFromTxn = (s) => {
    const m = String(s || '').trim().match(/^([A-Za-z][A-Za-z' .-]*),\s*([A-Za-z][A-Za-z' .-]*)$/);
    return m ? `${m[2].trim()} ${m[1].trim()}` : '';
};

// ── Stored billing data ──
// The job's Billing rows (Forecast_User_Data at BILLING_VERSION, tab
// 'Billing') parsed into what pricing reads: the frozen line snapshots per
// summary (`billed`), the not-billable lines a summary closed out at $0
// (`nbLines`), the per-line review/override meta, the summaries
// themselves, and the job's settings merged over the defaults. The Rates &
// Rules view parses its own fieldNames (rate/rule/rateset) out of the same
// rows — see billingRates.js.
export const parseBillingRows = (rows, jobKey) => {
    const billedMap = {};
    const nbLineMap = {};
    const metaMap = {};
    const invList = [];
    let stored = null;
    (rows || []).forEach((x) => {
        if (x.Forecast_Field_Name === 'line' && x.Forecast_Value) {
            let snap = null;
            try { snap = x.Notes ? JSON.parse(x.Notes) : null; } catch (e) { /* malformed blob — ignore */ }
            billedMap[x.Forecast_Row_ID] = { inv: x.Forecast_Value, snap };
        } else if (x.Forecast_Field_Name === 'nbline' && x.Forecast_Value) {
            // A not-billable line closed out on a summary: the summary's
            // period covered it and it bills $0. Its own field so every
            // reader of 'line' rows (totals, hours, exports) never sees it.
            let snap = null;
            try { snap = x.Notes ? JSON.parse(x.Notes) : null; } catch (e) { /* malformed blob — ignore */ }
            nbLineMap[x.Forecast_Row_ID] = { inv: x.Forecast_Value, snap };
        } else if (x.Forecast_Field_Name === 'meta') {
            let p = null;
            try { p = x.Notes ? JSON.parse(x.Notes) : null; } catch (e) { /* malformed blob — ignore */ }
            metaMap[x.Forecast_Row_ID] = { nb: !!(p && p.nb), bo: !!(p && p.bo), rr: p?.rr || '', rs: p?.rs || '', lc: p?.lc || '', pt: p?.pt === 'Y' || p?.pt === 'N' ? p.pt : '', h: Array.isArray(p?.h) ? p.h : [] };
        } else if (x.Forecast_Field_Name === 'invoice' && isSummaryRow(x)) {
            try {
                if (x.Notes) {
                    const inv = JSON.parse(x.Notes);
                    invList.push({ ...inv, status: normInvStatus(inv.status), h: Array.isArray(inv.h) ? inv.h : [] });
                }
            } catch (e) { /* malformed blob — ignore */ }
        } else if (x.Forecast_Field_Name === 'settings') {
            try { if (x.Notes) stored = JSON.parse(x.Notes); } catch (e) { /* malformed blob — ignore */ }
        }
    });
    invList.sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.no).localeCompare(String(a.no)));
    const storedMk = { ...((stored || {}).mk || {}) };
    // Jobs saved before the per-cost-type labor rates carry one Labor
    // rate — seed 51100/51200/51500 from it so the custom rate survives.
    const laborSeed = storedMk.Labor != null && storedMk['51100'] == null
        ? { 51100: storedMk.Labor, 51200: storedMk.Labor, 51500: storedMk.Labor }
        : {};
    // Equipment 10 was the pre-tier-billing default (cost + 10% on all
    // equipment) — jobs that saved settings back then weren't choosing
    // it, so let today's 12% not-on-the-rate-sheet fallback through.
    if (pNum(storedMk.Equipment) === 10) delete storedMk.Equipment;
    // A job under contract rules starts from its contract's per diem policy
    // (PB4: six consecutive workdays earn the seventh; Blitz: five earn the
    // sixth and seventh) — a policy saved on Rates & Rules still wins.
    const contractPd = jobKey != null && contractOn(stored || {}, jobKey) ? contractFor(jobKey).pd : {};
    const merged = {
        ...DEFAULT_SETTINGS,
        ...(stored || {}),
        mk: { ...DEFAULT_SETTINGS.mk, ...laborSeed, ...storedMk },
        // Jobs that saved settings before per diem had a policy carry no
        // pd blob — and a job that saved a partial one still gets the
        // missing fields.
        pd: { ...DEFAULT_SETTINGS.pd, ...contractPd, ...((stored || {}).pd || {}) },
        // Fuel surcharge settings (billingFuel) — jobs that saved before
        // the surcharge existed get it on, fuel purchases excluded.
        fuel: {
            ...DEFAULT_SETTINGS.fuel,
            ...((stored || {}).fuel || {}),
            q: { ...((((stored || {}).fuel) || {}).q || {}) },
        },
        // Pass-through rules (billingPassThru) — jobs that saved before
        // they existed get the freight/delivery defaults at cost.
        pt: normPt((stored || {}).pt),
        // Contract billability rules (billingContract) — jobs that never
        // set the toggle follow the contract's own job list.
        ct: { ...DEFAULT_SETTINGS.ct, ...((stored || {}).ct || {}) },
    };
    return { billed: billedMap, nbLines: nbLineMap, invoices: invList, settings: merged, metas: metaMap };
};

// Vendor-invoice match status per PO line, keyed `${PO_Number}|${PO_Line_Number}`
// from loadPOMatchStatus's rows; matches cost history rows on (PO_Number,
// Line_ID). Always live — invoice status keeps moving after a WIP version is
// cut, so it never comes from the PCV snapshot.
export const poStatusMap = (rows) => {
    const map = {};
    (Array.isArray(rows) ? rows : []).forEach((r) => {
        map[`${pNum(r.PO_Number)}|${pNum(r.PO_Line_Number)}`] = r;
    });
    return map;
};

// Distinct JDE labor classifications on the job's billable labor cost, for
// the Rates & Rules view's JDE → rate sheet classification map.
export const jdeLaborClassesOf = (jobCostDetail) => {
    const m = new Map();
    (jobCostDetail || []).forEach((r) => {
        if (!LABOR_BILLABLE_CT.includes(String(r?.Cost_Type_Code || ''))) return;
        const name = String(r.Labor_Classification_Description || '').trim();
        if (!name) return;
        const e = m.get(name) || { name, hrs: 0 };
        e.hrs += pNum(r.Total_Hours);
        m.set(name, e);
    });
    return [...m.values()].sort((a, b) => b.hrs - a.hrs);
};

// Distinct JDE equipment tiers on the job's fleet (52100) and rental
// (52200) rows, for the Rates & Rules view's tier → rate sheet map.
// Fleet hours come off the TE equipment timecard rows. Rental rows are AP
// voucher lines whose Total_Hours is the voucher quantity, so their hours
// come off HeavyJob instead (`rentalUsage`, billingRentalUsage) — a rental
// tier bills hours × the sheet rate like fleet does, and a tier only seen
// on a card (a unit whose PO sits on the other job) still gets a row. A
// tier with no fleet usage is tagged `rental`, and `ru` when HeavyJob has
// usage for it. Rentals only carry the tier
// code (the PCV snapshot has no rental description column) —
// billingEquipMap's dictionary fills those in.
export const jobEquipTiersOf = (jobCostDetail, rentalUsage) => {
    const m = new Map();
    (jobCostDetail || []).forEach((r) => {
        const ct = String(r?.Cost_Type_Code || '');
        if (ct !== '52100' && ct !== '52200') return;
        const code = String(r.Equipment_Classification_Tier_Name || r.Rental_Equipment_Classification_Tier_Name || '').trim();
        if (!code) return;
        const e = m.get(code) || { code, descr: '', hrs: 0, fleet: false };
        if (!e.descr) e.descr = String(r.Equipment_Classification_Tier_Name_Description || '').trim();
        if (ct === '52100') { e.fleet = true; e.hrs += pNum(r.Total_Hours); }
        m.set(code, e);
    });
    rentUsageTiers(rentalUsage).forEach(({ code, hrs }) => {
        const e = m.get(code) || { code, descr: '', hrs: 0, fleet: false };
        e.hrs += hrs;
        e.ru = true;
        m.set(code, e);
    });
    return [...m.values()]
        .map(({ fleet, ...e }) => ({ ...e, rental: !fleet }))
        .sort((a, b) => b.hrs - a.hrs);
};

// Tier code → the rate sheet equipment row it bills at. Saved mappings
// win; unsaved tiers fall back to the same auto-suggestion the Rates &
// Rules card shows, so the two views always agree. `hourly` is the $/hr
// billing rate — day (and each) rates assume the provisions' single
// 10-hour shift, so they bill at rate ÷ 10 per hour.
export const equipBillsOf = (billingUserRows, jobEquipTiers) => {
    const sched = mergeRateSchedule(billingUserRows);
    const storedMap = parseEquipMap(billingUserRows);
    const out = {};
    (jobEquipTiers || []).forEach(({ code, descr }) => {
        const id = storedMap[code] !== undefined ? storedMap[code] : suggestEquipRow(code, descr, sched.equip);
        const row = id ? sched.equip.find((r) => r.id === id) : null;
        if (!row) return;
        const rate = pNum(row.rate);
        out[code] = {
            name: row.name, rate, uom: row.uom || 'day', hourly: row.uom === 'hr' ? rate : rate / 10,
            // Does this tier's machine burn fuel? Drives the fuel
            // surcharge base (billingFuel) — a towed trailer or an
            // excavator attachment carries no adder.
            fu: row.fu !== false,
        };
    });
    return out;
};

// JDE classification → the rate sheet classification it bills as (plus the
// sheet's ST/OT rates). Saved mappings win, then the field workbook's own
// mapping (`fieldClassSug`, billingLaborMap's fieldSuggestions), then the
// same auto-suggestion the Rates & Rules card shows, so the views always
// agree.
export const billsAsOf = (billingUserRows, jdeLaborClasses, fieldClassSug = {}) => {
    const sched = mergeRateSchedule(billingUserRows);
    const storedMap = parseLaborMap(billingUserRows);
    const names = sched.labor.map((r) => r.name);
    const out = {};
    (jdeLaborClasses || []).forEach(({ name }) => {
        const fieldPick = (fieldClassSug || {})[normClass(name)];
        const volta = storedMap[name] !== undefined ? storedMap[name]
            : (fieldPick && names.includes(fieldPick)) ? fieldPick
                : suggestVoltaClass(name, names);
        const row = volta ? sched.labor.find((r) => r.name === volta) : null;
        // `saved`: pinned on the Rates & Rules card, so it outranks the
        // Force Report's per-person classification (personClsOf).
        if (row) out[name] = { name: volta, wage: pNum(row.wage), st: stRate(row.wage, sched.cfg), ot: otRate(row.wage, sched.cfg), saved: storedMap[name] !== undefined };
    });
    return out;
};

// Per-person billing classification off the client's Attendance & Force
// Report master (readAttendanceRoster rows: { id, billCls }) — the "Rate
// Sheet Billing Classification" project controls keeps current for each
// person. Staff ID → rate sheet class name, kept only where the name is a
// row on the job's rate schedule. Sits between a saved class mapping and
// the field workbook / alias guess: a person's own line on the Force Report
// beats a class-wide guess, but a class pinned on Rates & Rules still wins.
export const personClsOf = (roster, sheetLabor) => {
    const byName = (sheetLabor && sheetLabor.byName) || {};
    const out = {};
    (roster || []).forEach((r) => {
        const id = String(r?.id || '').trim();
        const name = String(r?.billCls || '').trim();
        if (id && name && byName[name]) out[id] = name;
    });
    return out;
};

// The job's rate config — the wage multiplier and OT factor the sheet bills
// at ({ m, otf }), with any per-job override folded in.
export const rateCfgOf = (billingUserRows) => mergeRateSchedule(billingUserRows).cfg;

// Rate sheet labor classes by name, for the per-line classification fix:
// a line whose JDE payroll class is just wrong (someone coded as the
// wrong craft) bills as the class picked on the line instead of what the
// Rates & Rules map resolves.
export const sheetLaborOf = (billingUserRows) => {
    const sched = mergeRateSchedule(billingUserRows);
    const list = sched.labor
        .map((r) => ({ name: r.name, wage: pNum(r.wage), st: stRate(r.wage, sched.cfg), ot: otRate(r.wage, sched.cfg) }))
        .filter((r) => r.name);
    const byName = {};
    list.forEach((r) => { byName[r.name] = r; });
    return { list, byName };
};

// The job's fuel surcharge index rows and fuel settings — what billingFuel
// prices the equipment fuel surcharge off.
export const fuelCtxOf = (billingUserRows, settings) => ({
    settings: (settings || {}).fuel,
    rows: mergeRateSchedule(billingUserRows).fuel,
});

// ── Labor: the person's actual wage, never the sheet's target wage ──
//
// The Service Order bills labor at the employee's ACTUAL wage × the
// multiplier (OT × the OT factor on top). The rate sheet's wage per class
// is a target for estimating only — it is never used to price a line
// (Seth, 10/13/26: the invoices priced at the target wage had to be
// revised). The class map still says which sheet class a line bills AS
// (the classification printed on the summary, and whether the line is
// priced at all); the wage comes from JDE.
//
// JDE's own cost line carries the wage: on a straight-time (51100) line
// Job_Cost ÷ Total_Hours is the person's base pay rate (checked on 54026070
// at version 124: 2,515 of 2,528 ST lines equal the F0618 pay rate to the
// cent; the rest are premium-paid hours, which the rate schedule counts as
// part of the actual wage). An overtime (51200) line's cost is 1.5 × base
// for hourly people but 1 × base for salaried ones, so OT takes the
// person's base from their ST lines that week (the nearest week with any,
// when the week has none — the same rule localserver/lib/jdePayRates.js
// applies to the F0618 file), and only falls back to cost ÷ hours ÷ 1.5
// for someone with no straight time on the job at all.
const weekEndOf = (d) => moment.utc(d).isoWeekday(7).format('YYYY-MM-DD');
export const laborBasesOf = (jobCostDetail) => {
    const by = {}; // staff id → week ending → { cost, hrs }
    (jobCostDetail || []).forEach((r) => {
        if (String(r?.Cost_Type_Code || '') !== '51100') return;
        const sid = String(r.Staff_ID || '').trim();
        const h = pNum(r.Total_Hours);
        const d = r.Service_Date || r.GL_Date;
        if (!sid || !h || !d) return;
        const we = weekEndOf(d);
        const p = (by[sid] || (by[sid] = {}))[we] || (by[sid][we] = { cost: 0, hrs: 0 });
        p.cost += pNum(r.Job_Cost); p.hrs += h;
    });
    const weeks = {};
    Object.keys(by).forEach((sid) => { weeks[sid] = Object.keys(by[sid]).sort(); });
    /** The person's base pay rate in the week `date` falls in, or null. */
    return (sid, date) => {
        const w = by[String(sid || '').trim()];
        if (!w || !date) return null;
        const we = weekEndOf(date);
        const own = w[we];
        if (own && own.hrs) return own.cost / own.hrs;
        const t = moment.utc(we);
        const near = weeks[String(sid).trim()]
            .map((x) => ({ x, dist: Math.abs(moment.utc(x).diff(t, 'days')) }))
            .sort((a, b) => a.dist - b.dist)[0];
        return near && w[near.x].hrs ? w[near.x].cost / w[near.x].hrs : null;
    };
};

// Project Director overtime bills at the straight rate (Service Order
// Attachment A) — the one class whose OT takes no OT factor.
export const OT_STRAIGHT_CLASS = 'Project Director';

/** The bill rate per hour for a labor line: wage × m, OT × otf on top. */
export const laborRateFor = (ct, wage, cfg, ba) => {
    // Number(), not pNum: pNum rounds to 2 decimals and would turn the
    // 1.633 multiplier into 1.63.
    const c = cfg || DEFAULT_RATE_CONFIG;
    const w = Number(wage) || 0;
    const m = Number(c.m) || DEFAULT_RATE_CONFIG.m;
    const otf = Number(c.otf) || DEFAULT_RATE_CONFIG.otf;
    if (!(w > 0)) return 0;
    if (ct === '51100') return round2(w * m);
    if (ct === '51200') return round2(w * m * (ba === OT_STRAIGHT_CLASS ? 1 : otf));
    return 0;
};

/** The wage a cost line was paid at: ST from the line itself, OT from the person's week. */
export const laborWageOf = ({ ct, cost, hrs, sid, date }, baseFor) => {
    // A payroll reversal posts negative hours and cost: the same wage, and
    // the bill (hrs × rate) comes out as the credit it is.
    if (!hrs) return 0;
    if (ct === '51100') return Math.abs(pNum(cost) / hrs);
    if (ct === '51200') {
        const b = baseFor ? baseFor(sid, date) : null;
        return b != null ? b : Math.abs(pNum(cost) / hrs / 1.5);
    }
    return 0;
};

// ── Transactions from live cost history ──
//
// Every 5xxxx cost history line the tab bills or reviews, priced. `ctx` is
// what the pricing reads: { billed, metas, settings, poStatus, billsAs,
// equipBills, sheetLabor, rateCfg, contractRules, contractJob, rentalUsage,
// rentalElsewhere } — each from the helpers above (rateCfg from rateCfgOf,
// the job's wage multiplier and OT factor; contractRules from
// billingContract's contractOn; contractJob the job key, which picks the
// job's Service Order; rentalUsage / rentalElsewhere billingRentalUsage's
// rentalUsageOf / rentalElsewhereOf, null / [] while they haven't loaded).
// Rental usage lines — HeavyJob hours on rented units, no cost of their own
// — are priced in the same pass, keyed ru_… (`rentUsage`).
export const priceCostRows = (jobCostDetail, ctx = {}) => {
    const billed = ctx.billed || {};
    const nbLines = ctx.nbLines || {};
    const metas = ctx.metas || {};
    const settings = ctx.settings || DEFAULT_SETTINGS;
    const poStatus = ctx.poStatus || {};
    const billsAs = ctx.billsAs || {};
    const equipBills = ctx.equipBills || {};
    const sheetLabor = ctx.sheetLabor || { list: [], byName: {} };
    const personCls = ctx.personCls || {};
    const rateCfg = ctx.rateCfg || DEFAULT_RATE_CONFIG;
    const baseFor = laborBasesOf(jobCostDetail);
    const contractRules = ctx.contractRules;
    const contractJob = ctx.contractJob;
    const list = (jobCostDetail || []).filter((r) => {
        const c = String(r?.Cost_Type_Code || '');
        if (c < '50000' || c >= '60000') return false;
        // 513xx/514xx/516xx/517xx (burden, taxes, insurance …) are never
        // billed — dropped at the source so they stay out of the stats,
        // filters and exports too, not just the grid.
        if (NEVER_BILLED_CT_PREFIX.some((p) => c.startsWith(p))) return false;
        return pNum(r.Job_Cost) !== 0;
    });
    // Rentals bill hours × the rate sheet rate off HeavyJob usage once the
    // job's cards carry rental units (billingRentalUsage). Usage lines take
    // the PO's own cost-history spelling so they group with its vouchers;
    // `ruUnits` is every unit with usage here, for the voucher flag below.
    const ruBasis = rentUsageFrom(settings, billed, ctx.rentalUsage);
    const poText = {};
    list.forEach((r) => {
        const p = String(r.PO_Number || '').trim();
        if (p) poText[p.replace(/^0+/, '')] = r.PO_Number;
    });
    const ruRows = ruBasis.on ? rentUsageCostRows(ctx.rentalUsage, { from: ruBasis.from, billed, poText }) : [];
    // Units with HeavyJob hours — here (live, or a usage line already on a
    // summary), or on another job's cards (the job that bills them) — whose
    // vouchers therefore stay off this invoice.
    const ruUnits = new Set([
        ...(ctx.rentalUsage || []).map((u) => u.code),
        ...ruRows.map((r) => r.Unit_Number),
    ]);
    const ruElsewhere = {};
    (ctx.rentalElsewhere || []).forEach((e) => {
        if (!ruUnits.has(e.code) && !ruElsewhere[e.code]) ruElsewhere[e.code] = e.job;
    });
    // POs with at least one unit in HeavyJob. A tiered rental PO with none
    // is a unit nobody set up there — it keeps billing cost + % (`ruMissing`).
    const ruPos = new Set([...ruUnits, ...Object.keys(ruElsewhere)]
        .map((c) => (/^PO-(\d+)-/.exec(c) || [])[1]).filter(Boolean));
    const seen = {};
    const seenJk = {};
    const out = [...list, ...ruRows].map((r) => {
        const ruLine = r._ru || null;
        const glDate = r.GL_Date ? moment.utc(r.GL_Date).format('YYYY-MM-DD') : '';
        // Payroll posts a whole week of timecards on one GL date, so a
        // GL-dated labor section reads as if everyone worked the same
        // day. Service_Date is the day the work actually happened —
        // that's the work date shown, sorted and filtered everywhere.
        // The legacy row hash stays on the GL date: it's part of every
        // previously billed line's identity.
        const date = r.Service_Date ? moment.utc(r.Service_Date).format('YYYY-MM-DD') : glDate;
        const base = [r.Line_ID, glDate, r.Phase_Code, r.Cost_Type_Code, Math.round(pNum(r.Job_Cost) * 100), r.PO_Number || '', r.Staff_ID || '', r.Supplier_Name || ''].join('|');
        seen[base] = (seen[base] || 0) + 1;
        // Legacy identity: a hash of the row's contents, so any JDE
        // correction (amount, reclass, date) changed the key and orphaned
        // the stored billed/meta state. Rows now carry the stable JDE doc
        // identity (Form_Job_Cost_History_Key — company/doc type/doc no/
        // batch/line, no amounts) and use it instead; corrections keep the
        // key, so the invoice link and audit trail follow the row. The
        // occurrence counter stays: duplicate fan-out rows are value-
        // identical and interchangeable. Rows billed under the old scheme
        // keep matching through the legacy fallback lookups below; new
        // writes always land on the bl2_ key.
        // A rental usage line carries its own key (unit|date|cost code).
        const legacyKey = ruLine ? ruLine.key : 'bl_' + hash(base + '#' + seen[base]);
        const jk = String(r.Form_Job_Cost_History_Key || '').trim();
        let key = legacyKey;
        if (jk) {
            seenJk[jk] = (seenJk[jk] || 0) + 1;
            key = 'bl2_' + jk + '#' + seenJk[jk];
        }
        const cls = bucketOf(r.Cost_Type_Code);
        const rec = billed[key] || billed[legacyKey];
        const meta = metas[key] || metas[legacyKey];
        // Vendor-invoice match status: cost history's Line_ID is the PO
        // line number on PO-driven (OV/PV) rows. A usage line isn't a
        // vendor document — its PO line's vouchers carry the status.
        const ms = r.PO_Number && !ruLine ? poStatus[`${pNum(r.PO_Number)}|${pNum(r.Line_ID)}`] : null;
        const vinv = !ms ? '' : ms.Match_Status === 'Partially Vouchered' ? 'Partial' : ms.Match_Status;
        const vinvDate = !ms ? '' :
            ms.Match_Status === 'Received' ? fDate(ms.Last_Received_Date) : fDate(ms.Last_Voucher_GL_Date);
        const vinvNo = ms?.Latest_Supplier_Invoice_Number || '';
        // Non-billable cost codes and reversed vendor invoices default the
        // flag on, but a saved billable-override (meta.bo) flips one back;
        // a line on an invoice can't also be not billable — billed wins
        // over both.
        const ccNb = NON_BILLABLE_CC.includes(r.Phase_Code || '');
        const hist = meta?.h || [];
        const rs = meta?.rs || '';
        const lastRev = [...hist].reverse().find((e) => REVIEW_EVENTS.includes(e.a)) || null;
        const cost = pNum(r.Job_Cost);
        const ct = r.Cost_Type_Code || '';
        // Pass-through delivery/freight (billingPassThru): the line's
        // own override, else the job's description / cost type rules.
        // Billed lines keep what their snapshot froze.
        // Usage lines rate-bill like fleet usage, which is never
        // pass-through (billingPassThru skips 52100).
        const ptOv = ruLine ? '' : meta?.pt || '';
        const ptLine = { cls, ct, ds: r.Remark_Description || '' };
        const ptWhy = ruLine ? '' : ptRuleMatch(ptLine, settings.pt);
        const pt = rec ? !!rec.snap?.pt : !ruLine && isPassThru(ptLine, settings.pt, ptOv);
        // What the contract says about this line (billingContract.js).
        // 'nb' rules default it off the invoice with the clause as the
        // reason; 'cost' rules bill it at cost with no markup; 'warn' rules
        // leave it billable and flag it; 'bill' rules just record why it
        // bills. A line on a summary was billed under whatever the rules
        // said then, so the rule is advisory there — the frozen snapshot
        // still governs.
        const sup = String(r.Supplier_Name || '').trim();
        const cr = contractRule({
            // A rental usage line is equipment usage billed off the rate
            // sheet — the contract reads it the way it reads fleet usage
            // (52100): the vehicles/equipment rule, and none of the
            // purchase-side rules (a monitor's usage day IS its $8 bill).
            ct: ruLine ? '52100' : r.Cost_Type_Code || '', cc: r.Phase_Code || '', ds: r.Remark_Description || '',
            cls, cost: pNum(r.Job_Cost), sup,
            jde: cls === 'Labor' ? String(r.Labor_Classification_Description || '').trim() : '',
            // Only a by-hand pass-through tag exempts a line — see contractRule.
            pt: ptOv === 'Y',
        }, contractRules, contractJob);
        const crNb = cr?.effect === 'nb';
        const eqTier = String(r.Equipment_Classification_Tier_Name || r.Rental_Equipment_Classification_Tier_Name || '').trim();
        // A rental voucher on a PO line whose unit has HeavyJob hours bills
        // through that usage once the job is on that basis, so the voucher
        // itself defaults off the invoice. Only the unit's own line: JDE
        // stamps the tier on every line of a rental PO, and its freight,
        // maintenance and other lines still bill on their own rules — as
        // does a unit never set up in HeavyJob. `ruUnit` is the unit's
        // HeavyJob code, for the reason.
        const unitCode = !ruLine && ct === RENTAL_CT ? rentUnitCode(r.PO_Number, r.Line_ID) : '';
        const ruDated = ruBasis.on && (!ruBasis.from || date >= ruBasis.from);
        const ruUnit = unitCode && ruDated && (ruUnits.has(unitCode) || ruElsewhere[unitCode]) ? unitCode : '';
        const ruMissing = !!unitCode && ruDated && !ruUnit && !!eqTier && !ruPos.has(unitCode.split('-')[1]);
        const autoNb = ccNb || vinv === 'Reversed' || crNb || !!ruUnit;
        // Closed not billable on a summary ('nbline'): the summary covered
        // the line's period and billed it $0, so it stays not billable
        // whatever its switch says until it comes off that summary.
        const nbRec = !rec ? (nbLines[key] || nbLines[legacyKey] || null) : null;
        const nbOwn = autoNb ? !meta?.bo : !!meta?.nb;
        const nb = !rec && (nbRec ? true : nbOwn);
        const nbReason = (nb && ((nbRec && nbRec.snap?.rr) || meta?.rr
            || (ccNb ? 'Non-billable cost code'
                : vinv === 'Reversed' ? 'Vendor invoice reversed'
                : crNb ? `${cr.label} (${cr.clause})`
                : ruUnit ? (ruElsewhere[ruUnit]
                    ? `Rental — bills on ${ruUnit}'s HeavyJob hours on ${ruElsewhere[ruUnit]}'s cards`
                    : `Rental — bills on ${ruUnit}'s HeavyJob hours`)
                : ''))) || '';
        // Labor never bills cost × markup — ST/OT bill hours × the rate
        // sheet classification rate (below) and per diem passes through
        // at cost (labor provision 5), which the 0 here reduces to. Only
        // the non-labor classes rate as a bucket from settings, and
        // pass-through lines at the job's pass-through %. A contract
        // at-cost rule (freight, hauling, affiliates) beats both: the
        // contract says no markup, whatever the job's settings say.
        const atCost = cr?.effect === 'cost';
        const mk = rec ? pNum(rec.snap?.m) : cls === 'Labor' || atCost ? 0 : pt ? settings.pt.mk : pNum(settings.mk[cls]);
        // Total_Hours drives labor billing and fleet (52100) rate
        // billing — those are timecard rows with real hours — and rental
        // usage lines, whose hours are the unit's HeavyJob card time.
        // Rental (52200) vouchers are AP lines whose Total_Hours is the
        // voucher line QUANTITY (a $22k month of crane rental posts as
        // "1 hour"), so a voucher never rate-bills — it stays cost ×
        // Equipment %, like fuel/repairs (52300/52400) whose
        // Total_Hours are unit quantities too.
        const hrs = cls === 'Labor' || ct === '52100' || ruLine ? pNum(r.Total_Hours) : 0;
        // GL document type JP = P-Card transactions (Dim_ERP_Codes DT/JP).
        const pcard = String(r.Document_Type || '').trim() === 'JP';
        // JDE payroll classification and the rate sheet classification it
        // bills as (via the Rates & Rules map) — labor rows only.
        const jde = cls === 'Labor' ? String(r.Labor_Classification_Description || '').trim() : '';
        // Per-line classification fix (meta.lc): this row's JDE payroll
        // class is wrong, so it bills as the rate sheet class picked on
        // the line instead of whatever the map resolves. A fix naming a
        // class that has since left the rate sheet prices nothing — the
        // line flags No Rate rather than quietly billing the class the
        // user said was wrong.
        const lcOv = (jde && meta?.lc) || '';
        // Fleet (52100) and rental usage bill hours × the rate sheet tier
        // rate (day rates ÷ 10 per the provisions' 10-hour shift) — the
        // rate IS the billing rate, no markup on top. Fleet tiers that
        // aren't on the rate sheet fall through to cost + the Equipment %
        // fallback; a usage line has no cost to fall back on, so it flags
        // No Rate below. Rental (52200) vouchers never rate-bill (hrs is 0
        // for them above). Billed lines keep their frozen snapshot.
        const tier = ct === '52100' || ct === '52200' ? eqTier : '';
        // A pass-through line never rate-bills — it's at cost by definition.
        const er = (!pt && tier && hrs && equipBills[tier]) || null;
        // Does this line's equipment burn fuel? Off the tier's rate sheet
        // row, so it resolves for rental (52200) vouchers and unmapped-hour
        // fleet rows too — not just the lines that rate-bill. Equipment
        // with no tier (fuel and repair cost types, misc equipment spend)
        // and tiers that map to nothing stay undefined: billingFuel keeps
        // those in the surcharge base and the summary flags them, rather
        // than dropping billable base on a row nobody classified.
        const fu = cls === 'Equipment' && tier && equipBills[tier] ? equipBills[tier].fu : undefined;
        // Billed as: labor's rate sheet classification, or the rate sheet
        // equipment row a tier bills at — frozen into the line snapshot.
        // Labor resolution order: the line's own override, a class mapping
        // pinned on Rates & Rules, the person's Force Report classification,
        // then the class-wide map (field workbook / alias guess).
        const pcName = jde && !lcOv && !billsAs[jde]?.saved ? (personCls[String(r.Staff_ID || '').trim()] || '') : '';
        const pcRow = pcName ? sheetLabor.byName[pcName] : null;
        const ba = jde
            ? (lcOv ? (sheetLabor.byName[lcOv] ? lcOv : '') : pcRow ? pcName : (billsAs[jde]?.name || ''))
            : ((er && er.name) || '');
        // Labor ST/OT bills hours × the person's actual wage × the job's
        // multiplier (laborRateFor) — never the sheet wage. The mapped
        // classification only decides whether the line is priced and what
        // it bills as. Per diem (51500) is a reimbursement at cost, so it
        // never rate-bills.
        const lr = jde ? (lcOv ? sheetLabor.byName[lcOv] : (pcRow || billsAs[jde])) : null;
        const wage = lr && (ct === '51100' || ct === '51200')
            ? laborWageOf({ ct, cost, hrs, sid: r.Staff_ID, date }, baseFor) : 0;
        // A mapped line with cost but no hours can't rate-bill; it keeps
        // the class's rate so "no hours" reads apart from "no rate".
        const laborRate = wage > 0 ? laborRateFor(ct, wage, rateCfg, ba)
            : lr ? (ct === '51200' ? lr.ot : ct === '51100' ? lr.st : 0) : 0;
        const rateLabor = (ct === '51100' || ct === '51200') && wage > 0 && laborRate > 0 && hrs !== 0;
        // An ST/OT line the sheet can't price — unmapped classification,
        // mapped to a rate-less row, or cost posted without hours — is
        // flagged rather than billed on some other basis: it stays off
        // summaries until the map (or the schedule) gives it a rate. So is
        // a rental usage line whose tier maps to nothing.
        const noRate = !rec && !nb && ((cls === 'Labor' && (ct === '51100' || ct === '51200') && !rateLabor) || (!!ruLine && !er));
        const hourly = rec ? rec.snap?.b != null : !!er || rateLabor;
        const rateAmt = er ? er.rate : laborRate;
        const rateUom = er ? er.uom : laborRate ? 'hr' : '';
        // Rate bills round to the cent per line — hrs × rate can land a
        // binary hair under the true cent, and db/company's pNum (inside
        // money()) TRUNCATES decimals, printing a penny short otherwise.
        const bill = nb ? 0
            : rec && rec.snap?.b != null ? pNum(rec.snap.b)
            : er ? round2(hrs * er.hourly)
            : rateLabor ? round2(hrs * laborRate)
            : noRate ? 0
            : cost * (1 + mk / 100);
        return {
            key, cls, cost, mk, date, nb, ccNb, autoNb, nbReason, rs, hist, lastRev, vinv, vinvDate, vinvNo, pcard, hrs, hourly, bill, jde, ba, lcOv, rateAmt, rateUom, tier, noRate,
            // A billed line keeps the "burns no fuel" it froze (fu: 0), as
            // its summary does — or the Cost Plus Forecast's per-line fuel
            // would charge the surcharge the invoice held out.
            fu: rec && rec.snap?.fu === 0 ? false : fu,
            // The wage the labor line was paid at (JDE), which the bill rate
            // is built on; frozen into the snapshot so a later class fix
            // re-prices at the same wage. A billed line keeps what it froze.
            wage: rec ? (rec.snap?.w != null ? pNum(rec.snap.w) : 0) : (wage > 0 ? Math.round(wage * 1000) / 1000 : 0),
            // The contract rule this line falls under, if any — the
            // reason it's off the invoice, or the flag asking a human.
            cr,
            // The line's saved billable override — the PO-level freight
            // rule below honours it like any automatic non-billable.
            bo: !!meta?.bo,
            sup,
            // A rental usage line (HeavyJob hours on a rented unit) and
            // the job its PO is charged to, when that isn't this one. A
            // rental voucher whose rent now bills on usage names the unit;
            // `ruMissing` marks one whose PO has no unit in HeavyJob at all.
            rentUsage: !!ruLine,
            poJob: ruLine && ruLine.poJob && ruLine.poJob !== String(contractJob || '') ? ruLine.poJob : '',
            ruUnit,
            ruMissing,
            pt, ptOv, ptWhy,
            inv: rec ? rec.inv : null,
            // The summary that closed this not-billable line out at $0, and
            // whether that summary is the only reason it doesn't bill.
            nbInv: nbRec ? nbRec.inv : null,
            nbByClose: !!nbRec && !nbOwn,
            // Fuel surcharge adder frozen when the line landed on a
            // summary; unbilled lines price it live (billingFuel).
            fs: rec && rec.snap?.fs != null ? Number(rec.snap.fs) : undefined,
            cc: r.Phase_Code || '',
            ccDesc: r.Phase_Code_Description || '',
            // The employee number, for views that key people by id
            // rather than name (billingQa's labor checks).
            sid: r.Staff_ID ? String(r.Staff_ID) : '',
            // Labor rows that don't resolve to a staff record (JDE's
            // Miscellaneous catch-all employee, reimbursement entries)
            // still carry the person as "Last, First" in the payroll
            // transaction description — recover the name from there
            // rather than printing the catch-all.
            who: (cls === 'Labor'
                && (!r.Staff_First_Last_Name || r.Staff_First_Last_Name === 'Miscellaneous')
                && nameFromTxn(r.Transaction_Description))
                || r.Staff_First_Last_Name || r.Supplier_Name || '',
            eq: [r.Unit_Number, r.Equipment_Classification_Tier_Name || r.Rental_Equipment_Classification_Tier_Name].filter(Boolean).join(' - '),
            // The same unit with its tier described rather than coded, for
            // the grid's EQUIPMENT column — the row's own description, else
            // billingEquipMap's dictionary (rentals only carry the code).
            eqDs: [r.Unit_Number, tierDescr(eqTier, r.Equipment_Classification_Tier_Name_Description) || eqTier].filter(Boolean).join(' - '),
            // The bare unit and the JDE line identity, for the
            // Reconciliation view's match to field ticket rows.
            unit: String(r.Unit_Number || '').trim(),
            jk,
            // JDE doc type (OV receipt, PV voucher, JE …) and GL date — the
            // cost basis popup lists a line's entries by them.
            dct: String(r.Document_Type || '').trim(),
            gl: glDate,
            po: r.PO_Number || '',
            // PO line number — cost history's Line_ID on PO-driven (OV/PV)
            // rows; meaningless without a PO, so blank there.
            pol: r.PO_Number && r.Line_ID != null && r.Line_ID !== '' ? String(pNum(r.Line_ID)) : '',
            ds: r.Remark_Description || '',
            ct,
            ctDesc: r.Cost_Type_Code_Description || r.Cost_Type_Description || '',
            // Link to the scanned vendor invoice in TAP. The cost history
            // view's own Media_Object_URL join is nearly always empty (it
            // joins TAP on the wrong key), so PO-driven rows fall back to
            // the TAP doc matched via the voucher's supplier invoice
            // number in the poMatchStatus rollup.
            pdf: r.Media_Object_URL || ms?.TAP_URL || '',
        };
    });
    // Freight follows the item it delivered (the 9/25 rubric): a freight
    // line on a PO whose every item is off the invoice by contract is off
    // it too. JDE files freight as its own PO line, so this can only be
    // read across the PO, never from the freight line alone. Tax rows are
    // neither item nor freight. Billed lines keep their snapshot, lines a
    // summary closed out stay as closed, and a saved billable override
    // still wins.
    if (contractRules) {
        const freightNb = contractRuleById('freightnb', contractJob);
        const byPo = {};
        out.forEach((t) => {
            if (!t.po || t.cls === 'Labor' || isFreightDs(t.ds) || /\btax/i.test(t.ds)) return;
            const g = byPo[t.po] || (byPo[t.po] = { items: 0, nb: 0 });
            g.items += 1;
            if (t.cr?.effect === 'nb') g.nb += 1;
        });
        out.forEach((t) => {
            const g = t.po ? byPo[t.po] : null;
            if (t.inv || t.nbInv || !g || !g.items || g.nb < g.items || t.cr?.effect === 'nb' || !isFreightDs(t.ds)) return;
            t.cr = freightNb;
            t.autoNb = true;
            t.nb = !t.bo;
            if (t.nb) {
                t.nbReason = t.nbReason || `${freightNb.label} (${freightNb.clause})`;
                t.bill = 0;
            }
        });
    }
    return out;
};

// ── The job's billable amount ──
//
// What the job's cost to date bills for: every billable line at its bill
// amount (frozen summary amount if invoiced, live rate-sheet / markup price
// if not) plus each equipment line's fuel surcharge share. This is the total
// the client cost report states and what the Overview VoltaGrid tab shows as
// revenue — a cost-plus job's revenue IS its billable cost.
//
//   bill      — billable amount, fuel included
//   fuel      — the fuel surcharge inside `bill`
//   billed    — the part already on a summary (frozen)
//   unbilled  — the part still to invoice (priced live)
//   cost      — the cost of the billable lines (what `bill` prices)
//   nbCost    — cost held off the invoice (not billable)
//   noRate    — ST/OT labor lines the rate sheet can't price yet: they
//               bill $0 here until Rates & Rules maps them, so `bill`
//               reads short by whatever they will bill
export const billableRevenue = (txns, fuelCtx = {}) => {
    const out = { bill: 0, fuel: 0, billed: 0, unbilled: 0, cost: 0, nbCost: 0, lines: 0, noRate: { n: 0, cost: 0, hrs: 0 } };
    (txns || []).forEach((t) => {
        if (t.nb) { out.nbCost += pNum(t.cost); return; }
        if (t.noRate) { out.noRate.n += 1; out.noRate.cost += pNum(t.cost); out.noRate.hrs += pNum(t.hrs); }
        const fuel = fuelLineAmt(t, fuelCtx);
        const amt = pNum(t.bill) + fuel;
        out.lines += 1;
        out.cost += pNum(t.cost);
        out.fuel += fuel;
        out.bill += amt;
        if (t.inv) out.billed += amt; else out.unbilled += amt;
    });
    return out;
};

// ── The cost to go, priced ──
//
// What cost that hasn't posted yet will bill for, under the same rules as
// the cost that has — the Cost Plus Forecast's Billable EAC prices each open
// PO line (its description and supplier are what the contract rules read:
// PPE, travel, freight, affiliates) and each forecast ETC line (a cost type
// on a cost code, nothing more) as if it had just posted. Nothing has been
// billed, reviewed or overridden on these lines, so no stored summary, meta
// or override applies; and no HeavyJob usage — a rental's hours don't exist
// yet.
//
//   lines   — [{ ct, cc, cost, ds, sup, po, pol, tier, date }]
//   ctx     — priceCostRows' ctx (settings, contractRules, contractJob,
//             equipBills, billsAs …): useBillingRevenue's `priceCtx`
//   fuelCtx — the job's fuel surcharge context
//
// One result per line, in order: { bill (fuel included), nb, noRate, how }.
// A cost type that never bills (burden, BYOD, truck and rig pay) never
// becomes a Billing line, so it comes back not billable. ST / OT labor bills
// hours × the rate sheet and cost alone carries no hours, so it comes back
// `noRate` for the caller to price off the job's own labor.
export const priceToGo = (lines, ctx = {}, fuelCtx = {}) => {
    const list = Array.isArray(lines) ? lines : [];
    const rows = list.map((l, i) => ({
        Cost_Type_Code: String(l.ct || ''), Job_Cost: pNum(l.cost), Phase_Code: l.cc || '',
        Remark_Description: l.ds || '', Supplier_Name: l.sup || '', PO_Number: l.po || '', Line_ID: l.pol != null ? l.pol : '',
        Equipment_Classification_Tier_Name: l.tier || '', Total_Hours: 0, Service_Date: l.date || null,
        Form_Job_Cost_History_Key: `togo${i}`,
    }));
    const priced = {};
    priceCostRows(rows, { ...ctx, billed: {}, nbLines: {}, metas: {}, poStatus: {}, rentalUsage: null, rentalElsewhere: [] })
        .forEach((t) => { priced[t.jk] = t; });
    return list.map((l, i) => {
        const t = priced[`togo${i}`];
        if (!t) {
            const never = NEVER_BILLED_CT_PREFIX.some((p) => String(l.ct || '').startsWith(p));
            return { bill: 0, nb: never, noRate: false, how: never ? 'Not billable — burden, BYOD, truck and rig pay never bill' : '' };
        }
        if (t.nb) return { bill: 0, nb: true, noRate: false, how: `Not billable — ${(t.cr && t.cr.effect === 'nb' && t.cr.label) || t.nbReason || 'by rule'}` };
        if (t.noRate) return { bill: 0, nb: false, noRate: true, how: 'Rate sheet — bills on hours' };
        const fuel = fuelLineAmt(t, fuelCtx || {});
        // A contract at-cost rule's label already says "at cost".
        const how = t.pt ? (t.mk ? `Pass-through, cost + ${t.mk}%` : 'Pass-through at cost')
            : t.mk ? `Cost + ${t.mk}%`
            : t.cr && t.cr.effect === 'cost' ? t.cr.label
            : 'At cost';
        return { bill: pNum(t.bill) + fuel, nb: false, noRate: false, how: fuel ? `${how} + fuel surcharge` : how };
    });
};
